import CSVLinkAsync from 'components/CSVLinkAsync';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'reactstrap';
import { useDebouncedState } from 'utils/util/hooks';
import { ICONS } from '../../../../../consts/consts';
import { Campaign } from '../../../../../types';
import { createCSVFileName } from '../../../../../utils/util/util';
import ModalEditor from '../../../Modals/ModalEditor';
import { useCalibration } from '../Calibration/CalibrationContext';
import { formatForCSV } from './helpers';
import {
  computeAvailableData,
  GroupField,
  generateFields,
  HeaderDescriptor,
} from './utils';

export interface TeamDashboardCsvHeaderDescriptor extends HeaderDescriptor {
  formatValueToCsv?: (value: any) => string | number;
  uiColumn?: string;
}

interface Props {
  data: object[];
  // for additional optional data fields corresponding to
  // the data in the data array by id
  personIdToMetadataMap: Record<number, any> | undefined;
  headers: TeamDashboardCsvHeaderDescriptor[];
  campaign: Campaign;
  allowedColumns?: string[];
  onDownloadRequested: (groupFields: GroupField[]) => void;
}

const TeamDashboardCSVDownload: FC<Props> = ({
  data,
  personIdToMetadataMap,
  headers,
  campaign,
  allowedColumns,
  onDownloadRequested,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const showIsSubmitting = useCallback(() => {
    setIsSubmitting(true);
  }, []);
  const [isDownloadRequested, setIsDownloadRequested] =
    useDebouncedState<boolean>(false, showIsSubmitting);

  const allowedColumnsSet = useMemo(
    () => (allowedColumns?.length ? new Set(allowedColumns) : undefined),
    [allowedColumns]
  );

  const handleOpen = useCallback(() => {
    // reset any isSubmitting state that was left on
    // from previous usage (as it is set on again when
    // isDownloadrequested is set to false per
    // useDebouncedState)
    setIsSubmitting(false);
  }, []);

  const [obj, setObj] = useState<object>({});

  const handleClick = useCallback(() => {
    setModalIsOpen(true);
  }, []);

  const handleCallback = useCallback(() => {
    onDownloadRequested(obj['group_fields']);
    setIsDownloadRequested(true);
  }, [obj, onDownloadRequested, setIsDownloadRequested]);

  const handleOnToggle = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const onChange = useCallback((value) => {
    setObj(value);
  }, []);

  const allowedHeadersList = useMemo(() => {
    return allowedColumnsSet
      ? headers.filter(
          (x) =>
            allowedColumnsSet.has(x.key) ||
            (x.uiColumn && allowedColumnsSet.has(x.uiColumn))
        )
      : headers;
  }, [headers, allowedColumnsSet]);

  const { filteredHeaders } = useMemo(() => {
    return computeAvailableData({
      selectedGroupFields: (obj['group_fields'] as GroupField[]) ?? [],
      headers: allowedHeadersList,
      intl,
    });
  }, [obj, intl, allowedHeadersList]);

  const allowedColumnsForDataGenerator = useMemo(() => {
    return new Set(allowedHeadersList.map((x) => x.key));
  }, [allowedHeadersList]);

  const inputs = useMemo(
    () => generateFields(campaign, allowedHeadersList, intl),
    [campaign, intl, allowedHeadersList]
  );

  const { surveyResponseIdToCalibrationItems } = useCalibration();

  const dataGenerator = useMemo(() => {
    if (isDownloadRequested) {
      return () =>
        formatForCSV(
          data,
          personIdToMetadataMap,
          headers,
          surveyResponseIdToCalibrationItems,
          allowedColumnsForDataGenerator,
          formatMessage
        );
    }

    return undefined;
  }, [
    data,
    personIdToMetadataMap,
    headers,
    surveyResponseIdToCalibrationItems,
    formatMessage,
    isDownloadRequested,
    allowedColumnsForDataGenerator,
  ]);

  const onDownloadComplete = useCallback(() => {
    setIsDownloadRequested(false);

    // close modal manually after download completes
    handleOnToggle();
  }, [handleOnToggle, setIsDownloadRequested]);

  return (
    <>
      <ModalEditor
        isOpen={modalIsOpen}
        toggle={handleOnToggle}
        title={formatMessage({
          id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_csv.title',
          defaultMessage: 'Select fields to export',
        })}
        object={obj}
        onChange={onChange}
        onOpened={handleOpen}
        isSubmitting={isSubmitting}
        keepOpenOnSuccess={true}
        callback={handleCallback}
        disabled={isDownloadRequested}
        submitText={formatMessage({
          id: 'app.views.widgets.dashboards.team_dashboard.team_dashboard_csv.submit_text',
          defaultMessage: 'Export',
        })}
        inputs={inputs}
        disableUnsavedChangesPrompt={true}
      />
      <Button
        color="light"
        className="btn btn-light btn-sm"
        onClick={handleClick}
      >
        <i className={ICONS.EXPORT + ' me-2'} />
        <FormattedMessage
          id="app.views.widgets.dashboards.team_dashboard.export_num_to_csv"
          defaultMessage="Export {num} people to CSV"
          values={{
            num: data.length,
          }}
        />
      </Button>
      {filteredHeaders && dataGenerator && (
        <CSVLinkAsync
          getData={dataGenerator}
          headers={filteredHeaders}
          filename={createCSVFileName()}
          onDownloadComplete={onDownloadComplete}
        />
      )}
    </>
  );
};

export default TeamDashboardCSVDownload;
